import { IConversionEvent } from '@/api/types/conversions';
import { IConversionChangesHistoryRow } from '@/api/types/conversions-report';
import { translateConversionStatus } from '@/utils/translate';

/** Подписи для `type` в GET .../autostats */
export const CONVERSION_EVENT_TYPE_LABELS: Record<string, string> = {
    created: 'Создание конверсии',
    status_updated: 'Обновление статуса',
    revenue_updated: 'Обновление дохода',
    payout_updated: 'Обновление выплаты',
    aff_invoice_added: 'Партнёрский счёт добавлен',
    aff_invoice_removed: 'Партнёрский счёт удалён',
    adv_invoice_added: 'Счёт рекламодателя добавлен',
    adv_invoice_removed: 'Счёт рекламодателя удалён',
    bonus_updated: 'Обновление бонуса',
    final_price_updated: 'Заморозка цены',
    sale_amount_updated: 'Обновление суммы продажи',
};

/** События, в которых старое/новое значение в таблице — денежная сумма */
const MONETARY_EVENT_TYPES = new Set<string>([
    'revenue_updated',
    'payout_updated',
    'bonus_updated',
    'final_price_updated',
    'sale_amount_updated',
]);

export function isConversionHistoryMonetaryEvent(eventType: string): boolean {
    return MONETARY_EVENT_TYPES.has(eventType);
}

const PAYLOAD_FIELD_LABELS: Record<string, string> = {
    invoice_id: 'ID счёта',
    invoice_period: 'Период счёта',
    advertiser_id: 'ID рекламодателя',
    advertiser_invoice_id: 'ID счёта рекламодателя',
    advertiser_invoice_period: 'Период счёта рекламодателя',
    advertiser_manager_id: 'ID менеджера рекла',
    advertiser_offer_goal_id: 'ID цели оффера рекламодателя',
    advertiser_offer_id: 'ID оффера рекламодателя',
    advertiser_subid: 'SubID рекламодателя',
    affiliate_id: 'ID партнёра',
    affiliate_invoice_id: 'ID счёта партнёра',
    affiliate_invoice_period: 'Период счёта',
    affiliate_manager_id: 'ID менеджера партнёра',
    conversion_approval_date: 'Дата аппрува',
    conversion_clone_of: 'Клон конверсии',
    conversion_creation_date: 'Дата создания',
    conversion_currency: 'Валюта конверсии',
    conversion_date: 'Дата клика',
    conversion_ip: 'IP конверсии',
    conversion_origin: 'Источник конверсии',
    conversion_payout: 'Выплата',
    conversion_referer_url: 'Referer',
    conversion_revenue: 'Доход',
    conversion_sale_amount: 'Сумма продажи',
    conversion_status: 'Статус конверсии',
    conversion_type: 'Тип конверсии',
    conversion_update_date: 'Дата обновления конверсии',
    conversion_user_agent: 'User-Agent',
    conversion_version: 'Версия конверсии',
    erir_token: 'ERIR token',
    hasoffers_event_id: 'HasOffers event ID',
    hasoffers_id: 'HasOffers ID',
    hasoffers_transaction_id: 'Transaction ID',
    tracker_click_hash: 'Хеш клика трекера',
    tracker_click_id: 'Click ID',
    tracker_user_id: 'ID клиента',
    transaction_id: 'Transaction ID',
    click_id: 'Click ID',
    client_id: 'ID клиента',
    conversion_id: 'ID конверсии',
    offer_id: 'ID оффера',
    goal_id: 'ID цели',
    master_account_id: 'ID мастера',
    buffer_account_id: 'ID буфера',
    buffer_account_name: 'Имя буфера',
    advertiser_name: 'Рекламодатель',
    offer_name: 'Оффер',
    goal_name: 'Цель',
    master_account_name: 'Имя мастера',
    affiliate_name: 'Имя партнёра',
    advertiser_manager_name: 'Менеджер рекла',
    affiliate_manager_name: 'Менеджер партнёра',
    creation_date: 'Дата создания',
    approval_date: 'Дата аппрува',
    click_date: 'Дата клика',
    adv_sub: 'SubID рекламодателя',
    payout: 'Выплата',
    revenue: 'Доход',
    profit: 'Прибыль',
    status: 'Статус',
    currency: 'Валюта',
    sale_amount: 'Сумма продажи',
};

function subidLabel(key: string): string | null {
    const m = /^affiliate_subid(\d+)$/.exec(key);
    if (m) return `SubID партнёра ${m[1]}`;
    return null;
}

export function translateConversionHistoryFieldKey(key: string): string {
    return PAYLOAD_FIELD_LABELS[key] ?? subidLabel(key) ?? key;
}

function formatConversionStatusValue(v: unknown): string {
    if (v === null || v === undefined || v === '') return '';
    const s = String(v);
    const { text } = translateConversionStatus(s);
    return text || s;
}

function invoicePayloadSubset(payload: Record<string, unknown>): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    if ('invoice_id' in payload) out.invoice_id = payload.invoice_id;
    if ('invoice_period' in payload) out.invoice_period = payload.invoice_period;
    return Object.keys(out).length ? out : payload;
}

/** API отдаёт `YYYY-MM-DD HH:mm:ss`; parseISO ожидает `T` между датой и временем. */
export function normalizeConversionEventRaisedAt(raisedAt: string): string {
    if (!raisedAt) return '';
    return raisedAt.includes('T') ? raisedAt : raisedAt.replace(' ', 'T');
}

function deriveOldNewDisplay(
    type: string,
    payload: Record<string, unknown>,
): { oldVal: unknown; newVal: unknown } {
    if (type === 'created') {
        return { oldVal: '', newVal: { ...payload } };
    }
    if (type === 'aff_invoice_added' || type === 'adv_invoice_added') {
        return { oldVal: '', newVal: invoicePayloadSubset(payload) };
    }
    if (type === 'aff_invoice_removed' || type === 'adv_invoice_removed') {
        return { oldVal: invoicePayloadSubset(payload), newVal: '' };
    }

    let oldV = payload.old_value;
    let newV = payload.new_value;

    if (type === 'status_updated') {
        oldV = formatConversionStatusValue(oldV);
        newV = formatConversionStatusValue(newV);
    }

    return {
        oldVal: oldV === undefined || oldV === null ? '' : oldV,
        newVal: newV === undefined || newV === null ? '' : newV,
    };
}

export function translateConversionEventType(type: string): string {
    return CONVERSION_EVENT_TYPE_LABELS[type] ?? type;
}

export function isExpandableHistoryValue(value: unknown): boolean {
    return (
        value !== null
        && typeof value === 'object'
        && !Array.isArray(value)
        && Object.keys(value as object).length > 0
    );
}

export function formatConversionHistoryScalar(value: unknown): string {
    if (value === null || value === undefined || value === '') return '';
    if (typeof value === 'boolean') return value ? 'Да' : 'Нет';
    if (typeof value === 'number' && Number.isFinite(value)) {
        return Number.isInteger(value)
            ? value.toLocaleString('ru-RU')
            : value.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    if (typeof value === 'string') return value;
    if (Array.isArray(value)) return value.map(formatConversionHistoryScalar).join(', ');
    return JSON.stringify(value);
}

/** Формат суммы для колонок истории: ru-RU, всегда две дробные цифры */
export function formatConversionHistoryAmount(value: unknown): string {
    if (value === null || value === undefined || value === '') return '';
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    if (typeof value === 'string' && value.trim() !== '') {
        const n = Number(value);
        if (Number.isFinite(n)) {
            return n.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        }
    }
    return formatConversionHistoryScalar(value);
}

export function flattenHistoryObject(
    obj: Record<string, unknown>,
): Array<{ key: string; val: unknown; rawKey: string }> {
    const rows: Array<{ key: string; val: unknown; rawKey: string }> = [];
    for (const [k, v] of Object.entries(obj)) {
        if (v === '') continue;
        if (v !== null && typeof v === 'object' && !Array.isArray(v)) {
            const inner = flattenHistoryObject(v as Record<string, unknown>);
            const parentLabel = translateConversionHistoryFieldKey(k);
            for (const { key, val, rawKey } of inner) {
                rows.push({ key: `${parentLabel} — ${key}`, val, rawKey });
            }
        } else {
            rows.push({ key: translateConversionHistoryFieldKey(k), val: v, rawKey: k });
        }
    }
    return rows;
}

const PERIOD_KEYS = new Set(['affiliate_invoice_period', 'invoice_period', 'advertiser_invoice_period']);

/** Числовые идентификаторы не форматируем через ru-RU (без пробелов-разделителей тысяч). */
function isConversionHistoryIdFieldKey(fieldKey: string | undefined): boolean {
    if (!fieldKey) return false;
    const k = fieldKey.toLowerCase();
    if (k === 'id') return true;
    return k.endsWith('_id') || k.endsWith('_ids');
}

function tryFormatRuDetailDate(value: unknown): string | null {
    if (typeof value !== 'string') return null;
    const s = value.trim();
    if (!/^\d{4}-\d{2}-\d{2}/.test(s)) return null;
    try {
        const iso = s.length === 10 ? `${s}T12:00:00` : s.replace(' ', 'T');
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return null;
        const formatted = new Intl.DateTimeFormat('ru-RU', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        }).format(d);
        return formatted.replace(/\s*г\.?\s*$/, '').trim();
    } catch {
        return null;
    }
}

export function formatConversionHistoryDetailCellValue(value: unknown, fieldKey?: string): string {
    if (value === null || value === undefined || value === '') return '-';
    if (fieldKey && PERIOD_KEYS.has(fieldKey)) {
        const formatted = tryFormatRuDetailDate(value);
        if (formatted) return formatted;
    }
    if (
        fieldKey
        && isConversionHistoryIdFieldKey(fieldKey)
        && typeof value === 'number'
        && Number.isFinite(value)
    ) {
        return String(value);
    }
    const t = formatConversionHistoryScalar(value);
    return t === '' ? '-' : t;
}

/** Порядок полей в выпадающем списке (как в макете). */
const CREATED_EVENT_DETAIL_KEY_ORDER: string[] = [
    'hasoffers_transaction_id',
    'tracker_click_id',
    'tracker_user_id',
    'conversion_id',
    'advertiser_id',
    'advertiser_name',
    'advertiser_offer_id',
    'offer_name',
    'advertiser_offer_goal_id',
    'goal_name',
    'advertiser_subid',
    'advertiser_manager_name',
    'advertiser_manager_id',
    'master_account_id',
    'master_account_name',
    'buffer_account_id',
    'buffer_account_name',
    'affiliate_id',
    'affiliate_name',
    'affiliate_manager_name',
    'affiliate_manager_id',
    'affiliate_invoice_id',
    'affiliate_invoice_period',
    'conversion_creation_date',
    'conversion_approval_date',
    'conversion_date',
];

function isFlatPrimitiveObject(obj: Record<string, unknown>): boolean {
    return Object.values(obj).every(
        (v) => v === null || v === undefined || v === '' || typeof v !== 'object' || Array.isArray(v),
    );
}

export function getConversionHistoryPopupRows(value: unknown): Array<{ label: string; text: string }> {
    if (!isExpandableHistoryValue(value)) return [];
    const raw = value as Record<string, unknown>;

    if (!isFlatPrimitiveObject(raw)) {
        return flattenHistoryObject(raw).map(({ key, val, rawKey }) => ({
            label: key,
            text: formatConversionHistoryDetailCellValue(val, rawKey),
        }));
    }

    const used = new Set<string>();
    const rows: Array<{ label: string; text: string }> = [];

    for (const key of CREATED_EVENT_DETAIL_KEY_ORDER) {
        if (!Object.prototype.hasOwnProperty.call(raw, key)) continue;
        used.add(key);
        rows.push({
            label: translateConversionHistoryFieldKey(key),
            text: formatConversionHistoryDetailCellValue(raw[key], key),
        });
    }

    const extraKeys = Object.keys(raw)
        .filter((k) => !used.has(k) && raw[k] !== '')
        .sort((a, b) => a.localeCompare(b));

    for (const key of extraKeys) {
        rows.push({
            label: translateConversionHistoryFieldKey(key),
            text: formatConversionHistoryDetailCellValue(raw[key], key),
        });
    }

    return rows;
}

export function normalizeConversionHistoryItem(
    raw: IConversionEvent,
    index: number,
): IConversionChangesHistoryRow {
    const { oldVal, newVal } = deriveOldNewDisplay(raw.type, raw.payload ?? {});
    const row: IConversionChangesHistoryRow = {
        id: `${raw.id}-${raw.raised_at}-${index}`,
        event_at: normalizeConversionEventRaisedAt(raw.raised_at),
        event_id: raw.id,
        event_type: raw.type,
        event_type_label: translateConversionEventType(raw.type),
        old_value: oldVal,
        new_value: newVal,
    };
    return row;
}
